import { PROGRAM_CATALOG, type ProgramCatalogEntry } from './programCatalog.js';
import { docsApps } from './apps.js';
import type { SemanticHubPage, SemanticPage } from './types.js';

type DocsAppDirectoryEntry = {
  id: string;
  slug: string;
  label: string;
  path: string;
  description: string;
  summary: string;
  folder: string;
  iconAsset: string;
  page: SemanticPage;
  hasCustomDocs: boolean;
};

const visiblePrograms = PROGRAM_CATALOG.filter((program) => !program.adminOnly);
const customDocsBySlug = new Map(docsApps.map((page) => [page.slug, page]));

const folderDescriptions: Record<string, string> = {
  AI: 'AI apps help people move from prompting into structured work, reusable workflows, and guided system actions.',
  Development: 'Development apps give builders a browser-native place to code, automate, inspect, and operate inside MapleOS.',
  System: 'System apps shape the environment itself, from files and settings to profile, network, and runtime control.',
  Office: 'Office apps turn MapleOS into a practical productivity workspace for documents, spreadsheets, slides, notes, and media.',
  Explore: 'Explore apps make MapleOS feel broader than a utility layer by adding browsing, mapping, screenshots, and media surfaces.',
  Games: 'Games and entertainment apps show that MapleOS is a full application environment, not only a productivity shell.',
  Core: 'Core apps support the central MapleOS experience and connect users to the rest of the platform.',
};

const folderQuickLinks: Record<string, string[]> = {
  AI: ['/docs/apps/ai-center', '/docs/apps/ai-blueprints', '/docs/concepts/ai-surfaces'],
  Development: ['/mapleos-for-developers', '/docs/use-cases/software-development', '/docs/concepts/ai-workflows'],
  System: ['/docs/apps/files', '/docs/apps/settings', '/docs/concepts/browser-native-os'],
  Office: ['/docs/apps/office', '/docs/use-cases/personal-productivity', '/docs/use-cases/content-creation'],
  Explore: ['/docs/use-cases/research', '/mapleos-for-consumers', '/docs/apps/files'],
  Games: ['/docs/apps/games', '/mapleos-for-consumers', '/mapleos-vs-chromeos'],
  Core: ['/docs', '/what-is-mapleos', '/ai-operating-system'],
};

const folderRelatedLinks: Record<string, string[]> = {
  AI: ['/what-is-mapleos', '/ai-operating-system', '/docs/apps/knowledge-base', '/docs/use-cases/research', '/docs/use-cases/business-automation', '/docs/apps'],
  Development: ['/what-is-mapleos', '/mapleos-for-developers', '/docs/apps/files', '/docs/use-cases/software-development', '/docs/concepts/ai-workflows', '/docs/apps'],
  System: ['/what-is-mapleos', '/docs/apps/knowledge-base', '/docs/use-cases/personal-productivity', '/docs/concepts/browser-native-os', '/mapleos-vs-windows', '/docs/apps'],
  Office: ['/what-is-mapleos', '/mapleos-for-consumers', '/docs/apps/knowledge-base', '/docs/use-cases/content-creation', '/docs/use-cases/education', '/docs/apps'],
  Explore: ['/what-is-mapleos', '/ai-surfaces', '/docs/use-cases/research', '/docs/use-cases/gaming-and-entertainment', '/mapleos-vs-chromeos', '/docs/apps'],
  Games: ['/what-is-mapleos', '/ai-surfaces', '/mapleos-for-consumers', '/docs/use-cases/gaming-and-entertainment', '/mapleos-vs-chromeos', '/docs/apps'],
  Core: ['/docs', '/what-is-mapleos', '/ai-operating-system', '/mapleos-for-consumers', '/mapleos-for-developers', '/docs/apps'],
};

function getFolder(program: ProgramCatalogEntry) {
  return program.folder || 'Core';
}

function buildGeneratedAppPage(program: ProgramCatalogEntry): SemanticPage {
  const folder = getFolder(program);
  const description = program.description || `${program.label} is a MapleOS application.`;

  return {
    id: `docs-app-${program.id}`,
    slug: program.id,
    path: `/docs/apps/${program.id}`,
    title: `${program.label} in MapleOS`,
    description: `Learn what ${program.label} does in MapleOS, how it fits the platform, and why it matters inside the MapleOS app environment.`,
    ogTitle: `${program.label} App | MapleOS`,
    ogDescription: description,
    summary: description,
    eyebrow: `${folder} App`,
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: program.label,
    quickLinkPaths: folderQuickLinks[folder] || folderQuickLinks.Core,
    relatedLinkPaths: folderRelatedLinks[folder] || folderRelatedLinks.Core,
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      {
        id: 'overview',
        title: `What ${program.label} is for`,
        paragraphs: [
          `${program.label} is part of the MapleOS ${folder.toLowerCase()} app catalog. ${description}`,
          `Inside MapleOS, apps are not isolated tabs. They are connected surfaces inside a browser-native operating environment, so people can move between context, files, workflows, and actions without losing their place.`,
        ],
      },
      {
        id: 'platform-role',
        title: `How ${program.label} fits the MapleOS system`,
        paragraphs: [
          `${program.label} contributes to the ${folder.toLowerCase()} layer of MapleOS. That means it is designed to work alongside related apps rather than acting like a standalone utility with no surrounding system story.`,
          `This is one of the strongest MapleOS differentiators: each app has a visible role in a broader environment, which makes the platform easier to learn, easier to market, and easier for search engines and AI systems to understand.`,
        ],
      },
      {
        id: 'value',
        title: `Why ${program.label} matters`,
        paragraphs: [
          `Apps like ${program.label} help MapleOS feel like a complete application environment instead of a single chat interface. They give users clear destinations for specific kinds of work and reinforce the idea that AI belongs inside understandable tools.`,
          `For teams, creators, and individual users, that makes MapleOS more memorable and more practical: the product can be explained through named apps, familiar tasks, and connected workflows rather than vague capability claims.`,
        ],
        bullets: [
          `Clear purpose inside the MapleOS ${folder.toLowerCase()} experience`,
          'Better discoverability through a stable docs route',
          'Stronger product storytelling for marketing and onboarding',
          'A more navigable app graph for people and machines',
        ],
      },
    ],
    faq: [
      {
        question: `What does ${program.label} do in MapleOS?`,
        answer: description,
      },
      {
        question: `Is ${program.label} a standalone product or part of MapleOS?`,
        answer: `${program.label} is presented here as part of the broader MapleOS application environment, where apps work together inside one browser-native system.`,
      },
      {
        question: `Why give ${program.label} its own docs page?`,
        answer: `A dedicated docs page makes ${program.label} easier to find in search, easier to explain in marketing, and easier to connect to related MapleOS concepts and workflows.`,
      },
    ],
  };
}

export const docsAppDirectoryEntries: DocsAppDirectoryEntry[] = visiblePrograms
  .map((program) => {
    const customPage = customDocsBySlug.get(program.id);
    const page = customPage || buildGeneratedAppPage(program);

    return {
      id: program.id,
      slug: program.id,
      label: program.label,
      path: `/docs/apps/${program.id}`,
      description: program.description || page.summary,
      summary: page.summary,
      folder: getFolder(program),
      iconAsset: program.iconAsset,
      page,
      hasCustomDocs: Boolean(customPage),
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

const hubGroups = Array.from(
  docsAppDirectoryEntries.reduce<Map<string, string[]>>((groups, entry) => {
    const existing = groups.get(entry.folder) || [];
    existing.push(entry.path);
    groups.set(entry.folder, existing);
    return groups;
  }, new Map())
).map(([folder, links]) => ({
  title: folder,
  description: folderDescriptions[folder] || folderDescriptions.Core,
  links: links.sort((a, b) => a.localeCompare(b)),
}));

export const docsAppsHubPage: SemanticHubPage = {
  id: 'docs-apps-hub',
  slug: 'apps',
  path: '/docs/apps',
  title: 'MapleOS Apps',
  description: 'Browse MapleOS apps through a marketing-friendly directory with app icons, descriptions, and dedicated pages for the MapleOS app catalog.',
  ogTitle: 'MapleOS Apps Directory',
  ogDescription: 'Explore the MapleOS app catalog with app icons, descriptions, and dedicated docs pages for AI, system, office, developer, media, and game apps.',
  summary: 'MapleOS includes AI tools, development surfaces, system utilities, office apps, media tools, exploration apps, and games inside one browser-native environment.',
  eyebrow: 'Apps',
  pageType: 'docs-hub',
  categoryLabel: 'Docs',
  breadcrumbLabel: 'Apps',
  quickLinkPaths: ['/docs/apps/ai-center', '/docs/apps/code-editor', '/docs/apps/music-player'],
  relatedLinkPaths: ['/docs', '/what-is-mapleos', '/ai-operating-system', '/mapleos-for-consumers', '/mapleos-for-developers', '/docs/use-cases/software-development'],
  schemaHints: ['collectionPage', 'faq'],
  sections: [
    {
      id: 'catalog',
      title: 'A full MapleOS app catalog',
      paragraphs: [
        'The MapleOS apps directory gives the platform a clearer product story by showing the app catalog as a connected system instead of a loose set of screens.',
        'Each app page helps users understand what that app is for, how it fits the broader MapleOS environment, and how the overall platform supports work, creativity, organization, and exploration.',
      ],
    },
    {
      id: 'marketing',
      title: 'Why the apps directory matters',
      paragraphs: [
        'A strong apps hub makes MapleOS easier to market because it turns abstract platform claims into visible product surfaces with names, icons, and roles.',
        'It also improves discoverability: search engines, AI systems, and people can all navigate from the apps directory into specific app pages that explain MapleOS in practical terms.',
      ],
    },
  ],
  hubGroups,
  faq: [
    {
      question: 'What is the MapleOS apps directory for?',
      answer: 'It gives MapleOS a clear, browsable app catalog so people can discover specific apps, understand what they do, and navigate directly into focused docs pages.',
    },
    {
      question: 'Does every app have its own page?',
      answer: 'Yes. Apps in the directory can resolve to dedicated docs pages so the MapleOS catalog is searchable, navigable, and easier to explain.',
    },
    {
      question: 'Can I search for apps like Music Player or Code Editor?',
      answer: 'Yes. The apps directory and docs navigation search can now surface individual MapleOS apps such as Music Player, Code Editor, AI Center, and many others.',
    },
  ],
};

export function getDocsAppDirectoryPage(slug: string) {
  return docsAppDirectoryEntries.find((entry) => entry.slug === slug)?.page ?? null;
}